import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import BridgeWaterfall from './BridgeWaterfall';
import EvidenceList from './EvidenceList';
import SignalMixBar from './SignalMixBar';
import {
  buildExecutiveSignificantItems,
  buildExecutiveSummaryRows,
  computeExecutiveKpis,
  excerptTwoSentences,
  formatAccounting,
  narrativeBody,
  narrativeCitations,
  synthesizeExecWaterfall
} from './boardPackHelpers';
import { EXEC_WATERFALL_SCREENS, basisTabLabel, formatMillions } from './boardScreenHelpers';

const varianceTone = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return '';
  return n < 0 ? 'bp-negative' : 'bp-positive';
};

const guardLabel = (board) => {
  const guard =
    board?.guardPass ??
    board?.guard_pass ??
    board?.company?.guardPass ??
    board?.company?.narrative?.guardPass;
  if (guard === true) return { text: 'Guard pass', tone: 'pass' };
  if (guard === false) return { text: 'Guard fail', tone: 'fail' };
  return { text: 'Guard pending', tone: 'pending' };
};

export const defaultExecutiveItem = (items = []) =>
  items.find((item) => item.id === 'earnings') || items[0] || null;

function ExecutiveSummaryTable({ rows, basisText, selectedId, onSelect }) {
  return (
    <div className="bp-exec-table-card">
      <div className="bp-exec-panel-head">
        <div>
          <div className="ace-eyebrow">Summary table</div>
          <strong>Income statement plus loan/deposit highlights</strong>
        </div>
        <small className="bp-muted">Click a row to focus the linked commentary and evidence.</small>
      </div>
      <table className="bp-table bp-exec-summary-table">
        <thead>
          <tr>
            <th>Line</th>
            <th className="bp-amount">Actual</th>
            <th className="bp-amount">{basisText}</th>
            <th className="bp-amount">Variance $</th>
            <th className="bp-amount">Variance %</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const active = selectedId === row.id;
            return (
              <tr
                key={row.id}
                className={`bp-exec-summary-row${active ? ' bp-exec-summary-row--active' : ''}`}
                onClick={() => onSelect(row.id)}
              >
                <td>
                  <span className="bp-exec-line-label">
                    <strong>{row.label}</strong>
                    <small>{row.section}</small>
                  </span>
                </td>
                <td className="bp-mono bp-amount">{formatAccounting(row.actual)}</td>
                <td className="bp-mono bp-amount">{formatAccounting(row.reference)}</td>
                <td className={`bp-mono bp-amount ${varianceTone(row.variance)}`}>
                  {formatAccounting(row.variance)}
                </td>
                <td className={`bp-mono bp-amount ${varianceTone(row.variance)}`}>
                  {row.variancePct}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const findingChipTone = (finding) => {
  const label = String(finding.classificationLabel || '').toLowerCase();
  if (label.includes('open') || label.includes('confirmed')) return 'signal';
  if (label.includes('noise') || label.includes('suppress')) return 'muted';
  return 'known';
};

// Top anomalous and especially dispositioned changes for the selected summary bucket,
// with the finding brief and disposition note, plus the existing workbench drill-in.
function DispositionFindings({ findings = [], onSelectFinding }) {
  const ranked = (findings || []).filter(
    (finding) => Math.abs(Number(finding.dollarImpact || 0)) > 0 || finding.brief
  );
  if (!ranked.length) return null;
  return (
    <ul className="bp-mix-finding-list">
      {ranked.slice(0, 4).map((finding) => (
        <li key={finding.id} className="bp-mix-finding">
          <div className="bp-mix-finding-head">
            <span className={`bp-mix-chip bp-mix-chip--${findingChipTone(finding)}`}>
              {finding.classificationLabel || 'Finding'}
            </span>
            <span className="bp-mix-finding-scope">
              <strong>{finding.glGroup || 'GL group'}</strong>
              <small>{finding.planningEntity || 'company'}</small>
            </span>
            <span className={`bp-mono bp-amount ${varianceTone(finding.dollarImpact)}`}>
              {formatMillions(finding.dollarImpact)}
            </span>
          </div>
          {finding.brief && <p className="bp-mix-finding-brief">{finding.brief}</p>}
          {finding.dispositionNote && (
            <p className="bp-mix-finding-note">
              Disposition{finding.verdict ? ` (${String(finding.verdict).replace(/_/g, ' ')})` : ''}: {finding.dispositionNote}
            </p>
          )}
          {onSelectFinding && (
            <button type="button" className="bp-inline-link" onClick={() => onSelectFinding(finding)}>
              Open finding {String(finding.id).slice(0, 6)} →
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

function DriverList({ drivers = [] }) {
  if (!drivers.length) return <small className="bp-muted">No material line drivers found for this row.</small>;
  return (
    <ul className="bp-exec-driver-list">
      {drivers.slice(0, 4).map((driver) => (
        <li key={driver.key}>
          <span>
            <strong>{driver.glGroup || 'GL line'}</strong>
            <small>{driver.planningEntity || 'company'}{driver.hasNarrative ? ' · narrated' : ''}</small>
          </span>
          <span className={`bp-mono bp-amount ${varianceTone(driver.variance)}`}>
            {formatAccounting(driver.variance)}
          </span>
        </li>
      ))}
    </ul>
  );
}

function SignificantItem({
  item,
  active,
  onSelect,
  onNavigateScreen,
  onSelectFinding,
  findingsById,
  expectationsByKey,
  evidence,
  onSelectBridge
}) {
  const [expanded, setExpanded] = useState(false);
  const { excerpt, hasMore } = excerptTwoSentences(item.body);
  const body = expanded ? item.body : excerpt || item.body;
  return (
    <article className={`bp-exec-item${active ? ' bp-exec-item--active' : ''}`}>
      <button type="button" className="bp-exec-item-trigger" onClick={() => onSelect(item.id)}>
        {active ? <ChevronDown size={13} aria-hidden /> : <ChevronRight size={13} aria-hidden />}
        <span>
          <strong>{item.label}</strong>
          <small>{item.section || 'variance driver'}</small>
        </span>
        <span className={`bp-mono bp-amount ${varianceTone(item.variance)}`}>
          {formatAccounting(item.variance)}
        </span>
      </button>
      <div className="bp-mix-inline">
        <SignalMixBar mix={item.signalMix} compact={!active} />
      </div>
      {active && (
        <div className="bp-exec-item-body">
          <p>{body}</p>
          <div className="bp-exec-action-row">
            {hasMore && (
              <button type="button" className="bp-inline-link" onClick={() => setExpanded((value) => !value)}>
                {expanded ? 'Collapse narrative' : 'Expand narrative'}
              </button>
            )}
            {item.screen && onNavigateScreen && (
              <button
                type="button"
                className="bp-inline-link"
                onClick={() => onNavigateScreen(item.screen, item.id)}
              >
                Open detail screen →
              </button>
            )}
          </div>
          <DispositionFindings findings={item.findings} onSelectFinding={onSelectFinding} />
          <DriverList drivers={item.drivers} />
          <EvidenceList
            citations={item.citations}
            evidence={evidence}
            findingsById={findingsById}
            expectationsByKey={expectationsByKey}
            onSelectFinding={onSelectFinding}
            onSelectBridge={
              onSelectBridge ? (bridgeInfo) => onSelectBridge(bridgeInfo, item.id) : undefined
            }
            compact
          />
        </div>
      )}
    </article>
  );
}

export default function BoardPackExecutiveSummary({
  board,
  basis,
  runId,
  findingsById,
  expectationsByKey = {},
  evidence = {},
  selectedItemId: controlledSelectedItemId = '',
  onSelectItem,
  onNavigateScreen,
  onSelectFinding,
  onSelectBridge
}) {
  const boardLines = board?.lines;
  const lines = useMemo(() => (Array.isArray(boardLines) ? boardLines : []), [boardLines]);
  const kpis = useMemo(() => computeExecutiveKpis(lines), [lines]);
  const companyNarrative = board?.company?.narrative || board?.company;
  const guard = guardLabel(board);
  const basisText = basisTabLabel(basis);
  const execWaterfall = useMemo(() => synthesizeExecWaterfall(kpis), [kpis]);
  const summaryRows = useMemo(() => buildExecutiveSummaryRows(lines), [lines]);
  const significantItems = useMemo(
    () => buildExecutiveSignificantItems({
      board,
      lines,
      summaryRows,
      findingsById,
      evidence,
      basis,
      limit: summaryRows.length
    }),
    [board, lines, summaryRows, findingsById, evidence, basis]
  );
  const [uncontrolledSelectedItemId, setUncontrolledSelectedItemId] = useState('');
  const selectedItemId = controlledSelectedItemId || uncontrolledSelectedItemId;
  const selectItem = (itemId) => {
    setUncontrolledSelectedItemId(itemId);
    onSelectItem?.(itemId);
  };
  const selectedItem =
    significantItems.find((item) => item.id === selectedItemId) ||
    defaultExecutiveItem(significantItems);
  const selectedId = selectedItem?.id || summaryRows[0]?.id;

  return (
    <section className="ace-panel bp-section">
      <div className="bp-exec-title-row">
        <div>
          <div className="ace-eyebrow">BP_01 <span aria-hidden>●</span> Executive summary</div>
          <h3>Executive review</h3>
        </div>
        <span className={`bp-guard-badge bp-guard-badge--${guard.tone}`}>{guard.text}</span>
      </div>

      <div className="bp-exec-canvas">
        <div className="bp-exec-column bp-exec-column--left">
          <div className="bp-exec-cell bp-exec-cell--matrix">
            <ExecutiveSummaryTable
              rows={summaryRows}
              basisText={basisText}
              selectedId={selectedId}
              onSelect={selectItem}
            />
          </div>

          <section className="bp-exec-bridge-panel bp-exec-cell bp-exec-cell--waterfall">
            <div className="bp-exec-panel-head">
              <div>
                <div className="ace-eyebrow">Summary waterfall</div>
                <strong>Earnings walk vs {basisText}</strong>
              </div>
              <small className="bp-muted">Each bridge family opens the detailed screen.</small>
            </div>
            <BridgeWaterfall bridge={execWaterfall} height={230} />
            {onNavigateScreen && (
              <div className="bp-waterfall-legend">
                {execWaterfall.components.map((component) => {
                  const screen = EXEC_WATERFALL_SCREENS[component.id];
                  if (!screen) return null;
                  return (
                    <button
                      key={component.id}
                      type="button"
                      className="bp-inline-link"
                      onClick={() => onNavigateScreen(screen, selectedId)}
                    >
                      {component.label}
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        <div className="bp-exec-column bp-exec-column--right">
          <section className="bp-exec-selected bp-exec-cell bp-exec-cell--narrative">
            <div className="bp-exec-panel-head">
              <div>
                <div className="ace-eyebrow">Summary narrative</div>
                <strong>{selectedItem?.label || 'Company narrative'}</strong>
              </div>
              {selectedItem && (
                <span className={`bp-mono bp-amount ${varianceTone(selectedItem.variance)}`}>
                  {formatAccounting(selectedItem.variance)}
                </span>
              )}
            </div>
            <p className="bp-commentary-body">
              {selectedItem?.body || narrativeBody(companyNarrative) || 'No company narrative returned for this run.'}
            </p>
            <EvidenceList
              citations={selectedItem?.citations || narrativeCitations(companyNarrative)}
              evidence={evidence}
              findingsById={findingsById}
              expectationsByKey={expectationsByKey}
              onSelectFinding={onSelectFinding}
              onSelectBridge={
                onSelectBridge ? (bridgeInfo) => onSelectBridge(bridgeInfo, selectedId) : undefined
              }
            />
          </section>

          <section className="bp-exec-significant bp-exec-cell bp-exec-cell--drilldown">
            <div className="bp-exec-panel-head">
              <div>
                <div className="ace-eyebrow">Drilldown hierarchy</div>
                <strong>Significant items, anomalies, and dispositions</strong>
              </div>
              <small className="bp-muted">Narrative is linked to summary rows and opens the underlying activity.</small>
            </div>
            <div className="bp-exec-item-list">
              {significantItems.map((item) => (
                <SignificantItem
                  key={item.id}
                  item={item}
                  active={selectedId === item.id}
                  onSelect={selectItem}
                  onNavigateScreen={onNavigateScreen}
                  onSelectFinding={onSelectFinding}
                  findingsById={findingsById}
                  expectationsByKey={expectationsByKey}
                  evidence={evidence}
                  onSelectBridge={onSelectBridge}
                />
              ))}
              {!significantItems.length && (
                <p className="bp-muted">No significant items returned for this run.</p>
              )}
            </div>
          </section>
        </div>
      </div>
    </section>
  );
}
